import {useState} from "react";
import {useQuery} from "react-query";
import dayjs from "dayjs";
import axios from "services/axios";

function mapApiItemToRow(item, index) {
	const fullName = item?.user?.fullName || [item?.user?.firstName, item?.user?.lastName].filter(Boolean).join(" ");
	const staffName = fullName || item?.staffName || item?.name || "-";
	const position = item?.role || item?.position || "-";

	const dateVal = item?.checkInDate || item?.date || item?.createdAt || null;
	// Format check-in/check-out from timestamp fields to HH:mm:ss
	const checkInTs = item?.checkInAt || null;
	const checkOutTs = item?.checkOutAt || null;
	const checkIn = checkInTs ? dayjs(checkInTs).format("HH:mm:ss") : null;
	const checkOut = checkOutTs ? dayjs(checkOutTs).format("HH:mm:ss") : null;
	const statusVal = item?.status ?? (checkIn ? "Hadir" : "Alpa");

	const date = dateVal ? dayjs(dateVal).format("YYYY-MM-DD") : "-";

	return {
		id: item?.id ?? index + 1,
		userId: item?.userId ?? item?.user?.id ?? null,
		idSerial: index + 1,
		staffName,
		position,
		date,
		checkIn,
		checkOut,
		checkInAt: checkInTs,
		checkOutAt: checkOutTs,
		status: statusVal ?? "-",
		hospital: item?.hospital || null,
	};
}

async function getAttendanceUser(userId, filter) {
	const {
		hospitalId,
		hospitalIds,
		date,
		startDate,
		endDate,
		page = 1,
		limit = 10,
		search = "",
		orderBy = "checkInDate",
		sortBy = "DESC",
	} = filter || {};

	const rangeFrom = startDate || date;
	const rangeTo = endDate || date;

	const params = {
		...(hospitalId ? {hospitalId} : {}),
		search: search || undefined,
		orderBy,
		sortBy,
		limit,
		page,
	};

	if (Array.isArray(hospitalIds) && hospitalIds.length > 0) {
		params.hospitalIds = hospitalIds.join(",");
	}

	if (rangeFrom && rangeTo) {
		params.checkInDate = `${rangeFrom},${rangeTo}`;
	}

	const {data: resp} = await axios.get(`/staff/attendances/user/${userId}`, {params});
	const payload = resp || {};
	const listData = payload?.data || payload || {};
	const apiRows = Array.isArray(listData?.rows) ? listData.rows : Array.isArray(payload?.rows) ? payload.rows : [];
	const rows = apiRows.map((it, idx) => mapApiItemToRow(it, idx));

	const totalItems = listData?.totalItems ?? payload?.totalItems ?? rows.length;
	const totalPages = listData?.totalPages ?? payload?.totalPages ?? 1;
	const currentPage = listData?.currentPage ?? payload?.currentPage ?? page;
	const summary = listData?.summary ?? payload?.summary ?? null;

	return {
		success: true,
		data: {
			rows,
			totalItems,
			totalPages,
			currentPage,
			summary,
		},
	};
}

export function useUserAttendanceSuperAdmin(userId) {
	const [filter, setFilter] = useState({
		hospitalId: null,
		hospitalIds: [],
		date: undefined,
		page: 1,
		limit: 10,
		search: "",
		orderBy: "checkInDate",
		sortBy: "DESC",
	});
	const fallback = {success: true, data: {rows: [], totalItems: 0, totalPages: 0, currentPage: 1, summary: null}};

	const {
		data = fallback,
		isLoading,
		isError,
		error,
	} = useQuery(["attendance-user-superadmin", userId, filter], async () => getAttendanceUser(userId, filter), {
		enabled: !!userId,
		keepPreviousData: true,
	});

	return {data, isLoading, isError, error, filter, setFilter};
}

async function getAttendanceUserTotalDuration(userId, filter) {
	const {
		hospitalId,
		hospitalIds,
		date,
		startDate,
		endDate,
		page = 1,
		limit = 10,
		search = "",
		orderBy = "checkInDate",
		sortBy = "DESC",
	} = filter || {};

	const rangeFrom = startDate || date;
	const rangeTo = endDate || date;

	const params = {
		...(hospitalId ? {hospitalId} : {}),
		search: search || undefined,
		orderBy,
		sortBy,
		limit,
		page,
	};

	if (Array.isArray(hospitalIds) && hospitalIds.length > 0) {
		params.hospitalIds = hospitalIds.join(",");
	}

	if (rangeFrom && rangeTo) {
		params.checkInDate = `${rangeFrom},${rangeTo}`;
	}

	const {data: resp} = await axios.get(`/staff/attendances/user/${userId}/totalDuration`, {params});

	return resp;
}

export function useUserAttendanceTotalDuration(userId) {
	const [filter, setFilter] = useState({
		hospitalId: null,
		hospitalIds: [],
		date: undefined,
		page: 1,
		limit: 10,
		search: "",
		orderBy: "checkInDate",
		sortBy: "DESC",
	});
	const fallback = {success: true, data: {rows: [], totalItems: 0, totalPages: 0, currentPage: 1, summary: null}};

	const {
		data = fallback,
		isLoading,
		isError,
		error,
	} = useQuery(
		["attendance-user-total-duration", userId, filter],
		async () => getAttendanceUserTotalDuration(userId, filter),
		{
			enabled: !!userId,
			keepPreviousData: true,
		},
	);

	return {data, isLoading, isError, error, filter, setFilter};
}
